import { z } from 'zod';
import type {
  ApiError,
  UpdateWorkspaceSettingsRequest,
  WorkspaceSettingsResponse,
} from '@/shared/api';
import { replyAddressSchema } from '@/shared/lib/replyAddress';

/**
 * Spiegelt UpdateWorkspaceSettingsRequestValidator. Ob der Server eine Zeitzone kennt, prüft nur
 * er; die Auswahl kommt aus dem Browser und kann davon abweichen.
 */
export const workspaceSettingsSchema = z.object({
  name: z.string().trim().min(1, 'Pflichtfeld').max(200, 'Max. 200 Zeichen'),
  timeZone: z.string().min(1, 'Zeitzone wählen').max(64, 'Max. 64 Zeichen'),
  defaultCurrency: z.string().regex(/^[A-Za-z]{3}$/, 'Währung wählen'),
  contactEmail: replyAddressSchema,
});

export type WorkspaceSettingsValues = z.infer<typeof workspaceSettingsSchema>;

export function toFormValues(settings: WorkspaceSettingsResponse): WorkspaceSettingsValues {
  return {
    name: settings.name,
    timeZone: settings.timeZone ?? '',
    defaultCurrency: settings.defaultCurrency ?? '',
    contactEmail: settings.contactEmail ?? '',
  };
}

export function toRequest(values: WorkspaceSettingsValues): UpdateWorkspaceSettingsRequest {
  const contactEmail = values.contactEmail.trim();
  return {
    name: values.name.trim(),
    timeZone: values.timeZone,
    defaultCurrency: values.defaultCurrency.toUpperCase(),
    contactEmail: contactEmail === '' ? null : contactEmail,
  };
}

const FIELD_REJECTIONS: Record<string, [keyof WorkspaceSettingsValues, string]> = {
  name: ['name', 'Der Name wurde nicht angenommen.'],
  timezone: ['timeZone', 'Diese Zeitzone kennt der Server nicht. Bitte wählen Sie eine andere.'],
  defaultcurrency: ['defaultCurrency', 'Diese Währung wurde nicht angenommen.'],
  contactemail: ['contactEmail', 'Diese E-Mail-Adresse wurde nicht angenommen.'],
};

/**
 * Feldablehnungen des Servers als deutsche Sätze am jeweiligen Feld — seine Meldungen sind
 * englisch, und der Schlüssel kann je nach Serialisierung groß oder klein beginnen.
 */
export function serverFieldErrors(
  problem: ApiError | null,
): [keyof WorkspaceSettingsValues, string][] {
  return Object.keys(problem?.fieldErrors ?? {})
    .map((key) => FIELD_REJECTIONS[key.toLowerCase()])
    .filter((entry): entry is [keyof WorkspaceSettingsValues, string] => entry !== undefined);
}

/** Der Satz unter dem Formular. Ohne Antwort ist offen, ob gespeichert wurde. */
export function saveFailureMessage(problem: ApiError): string {
  if (problem.status === 0) {
    return 'Keine Antwort vom Server – ob gespeichert wurde, ist offen. Bitte prüfen Sie die Verbindung.';
  }
  if (problem.status === 403) return 'Dafür fehlt Ihnen die Berechtigung.';
  if (problem.fieldErrors)
    return 'Die Angaben wurden nicht angenommen. Bitte prüfen Sie die Felder.';
  return 'Die Änderung wurde nicht gespeichert. Bitte versuchen Sie es erneut.';
}

/** Warum die Stammdaten fehlen — nie bloß „leer“. Ohne gelesenen Grund ein neutraler Satz. */
export function loadFailureMessage(problem: ApiError | null): string {
  if (!problem) return 'Bitte versuchen Sie es erneut.';
  if (problem.status === 0) {
    return 'Der Server hat nicht geantwortet. Bitte prüfen Sie die Verbindung.';
  }
  if (problem.status === 403) return 'Dafür fehlt Ihnen die Berechtigung.';
  return problem.detail ?? problem.title;
}
